import { useState } from 'react';
import { Icon, Loader, Button, Checkbox } from 'semantic-ui-react';

/**
 * Grid display of person image history
 * Shows recent uploaded person images with selection and delete options
 * Includes bulk edit mode for multi-select deletion
 */
const ImageHistoryGrid = ({
    personImages,
    activePersonImage,
    activePersonImageId,
    isLoadingImages,
    isCanvasIntegrated,
    onSelectPersonImage,
    onDeleteClick,
    onBulkDelete
}) => {
    const [isBulkEditMode, setIsBulkEditMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Combine backend images with localStorage images
    const isLoggedIn = !!localStorage.getItem('token');
    const guestImages = !isLoggedIn ? JSON.parse(localStorage.getItem('guestPersonImages') || '[]') : [];
    const allImages = isLoggedIn ? personImages : guestImages;

    const toggleBulkEditMode = () => {
        if (isBulkEditMode) {
            // Exiting bulk edit mode - clear selections
            setSelectedIds(new Set());
        }
        setIsBulkEditMode(!isBulkEditMode);
    };

    const toggleImageSelection = (imgId, e) => {
        e.stopPropagation();
        const newSelected = new Set(selectedIds);
        if (newSelected.has(imgId)) {
            newSelected.delete(imgId);
        } else {
            newSelected.add(imgId);
        }
        setSelectedIds(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedIds.size === allImages.length) {
            // Deselect all
            setSelectedIds(new Set());
        } else {
            // Select all
            setSelectedIds(new Set(allImages.map(img => img.id)));
        }
    };

    const handleBulkDelete = () => {
        if (selectedIds.size > 0 && onBulkDelete) {
            onBulkDelete(Array.from(selectedIds));
            setSelectedIds(new Set());
            setIsBulkEditMode(false);
        }
    };

    const hasImages = allImages && allImages.length > 0;

    return (
        <div className="image-history-section">
            <div className="history-header" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px'
            }}>
                <h5 style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#374151' }}>Recent Images</h5>
                {hasImages && (
                    <span
                        onClick={toggleBulkEditMode}
                        style={{
                            fontSize: '12px',
                            color: isBulkEditMode ? '#ef4444' : '#6b7280',
                            cursor: 'pointer',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'color 0.15s ease'
                        }}
                    >
                        {isBulkEditMode ? 'Cancel' : 'Edit'}
                    </span>
                )}
            </div>

            {/* Bulk Edit Controls */}
            {isBulkEditMode && hasImages && (
                <div className="bulk-edit-controls" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    padding: '8px 12px',
                    background: '#fafafa',
                    borderRadius: '10px',
                    border: '1px solid #f0f0f0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox
                            checked={selectedIds.size === allImages.length && allImages.length > 0}
                            indeterminate={selectedIds.size > 0 && selectedIds.size < allImages.length}
                            onChange={handleSelectAll}
                            label={<label style={{ fontSize: '12px', color: '#666' }}>Select All</label>}
                        />
                        {selectedIds.size > 0 && (
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                ({selectedIds.size} selected)
                            </span>
                        )}
                    </div>
                    <Button
                        color="red"
                        size="mini"
                        disabled={selectedIds.size === 0}
                        onClick={handleBulkDelete}
                        style={{ padding: '6px 12px' }}
                    >
                        <Icon name="trash" />
                        Delete ({selectedIds.size})
                    </Button>
                </div>
            )}

            <div className="history-grid">
                {isLoadingImages ? (
                    <div className="history-placeholder">
                        <Loader active inline size="small" />
                        <small>Loading your images...</small>
                    </div>
                ) : hasImages ? (
                    allImages.map((img) => {
                        const shouldUseRemoved = img.useRemovedImage !== false && img.removedBgImage?.url;
                        const displayImageUrl = shouldUseRemoved
                            ? img.removedBgImage.url
                            : (img.thumbnail?.url || img.image?.url);
                        const isSelected = selectedIds.has(img.id);

                        return (
                            <div
                                key={img.id}
                                className={`history-item ${(activePersonImage?.id === img.id || activePersonImageId === img.id) && isCanvasIntegrated ? 'active' : ''} ${img.isDefault ? 'default-image' : ''} ${isSelected ? 'selected' : ''}`}
                                onClick={() => isBulkEditMode ? toggleImageSelection(img.id, { stopPropagation: () => { } }) : onSelectPersonImage(img)}
                                title={img.displayName || img.fileName || 'Person image'}
                                style={isSelected ? { borderColor: '#db2828', borderWidth: '3px' } : undefined}
                            >
                                {/* Selection checkbox in bulk edit mode */}
                                {isBulkEditMode && (
                                    <div
                                        className="selection-checkbox"
                                        style={{
                                            position: 'absolute',
                                            top: '4px',
                                            left: '4px',
                                            zIndex: 4,
                                            background: 'white',
                                            borderRadius: '4px',
                                            padding: '2px'
                                        }}
                                        onClick={(e) => toggleImageSelection(img.id, e)}
                                    >
                                        <Checkbox checked={isSelected} />
                                    </div>
                                )}

                                <img
                                    src={displayImageUrl}
                                    alt={img.displayName || img.fileName || 'Person image'}
                                />
                                {img.isDefault && !isBulkEditMode && (
                                    <div className="default-badge">
                                        <Icon name="star" />
                                    </div>
                                )}
                                {!isBulkEditMode && (
                                    <button
                                        className="delete-person-icon-btn"
                                        onClick={(e) => onDeleteClick(e, img)}
                                        title="Delete person image"
                                    >
                                        <Icon name="close" />
                                    </button>
                                )}
                                <div className="image-label">
                                    {img.displayName || new Date(img.createdAt).toLocaleDateString()}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="history-placeholder">
                        <Icon name="history" />
                        <small>Your recent person images will appear here</small>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageHistoryGrid;
